<template>
  <div class="prl-page">

    <div v-if="loading" class="prl-loading">
      <i class="fas fa-spinner fa-spin fa-2x gold"></i>
      <p class="mt-3 small text-muted">Loading...</p>
    </div>

    <div v-else-if="!processingRequest" class="prl-empty">
      <i class="fas fa-bell-slash prl-empty__icon"></i>
      <p class="prl-empty__text">Notification not found.</p>
      <router-link to="/dashboard" class="prl-btn-gold">Go to Dashboard</router-link>
    </div>

    <div v-else class="prl-card">

      <!-- Hero: avatar + name + location -->
      <div class="prl-hero">
        <div class="prl-hero__avatar-wrap">
          <img
            :src="processingRequest.sender.profile_image || 'https://media.iamconnected.com/profile_images/default.png'"
            class="prl-hero__avatar"
            alt=""
          />
          <span class="prl-hero__badge"><i class="fas fa-hands-helping"></i></span>
        </div>
        <div>
          <p class="prl-hero__name" v-text="processingRequest.sender.full_name"></p>
          <p v-if="senderLocation" class="prl-hero__location">
            <i class="fas fa-map-marker-alt mr-1"></i>{{ senderLocation }}
          </p>
        </div>
      </div>

      <!-- Message block -->
      <div class="prl-message">
        <div class="prl-message__header">
          <i class="fas fa-hands-helping prl-message__icon"></i>
          <span class="prl-message__request"><strong>{{ processingRequest.sender.full_name }}</strong> has requested your support to hold space for a process.</span>
        </div>
        <div class="prl-message__body">
          <i class="fas fa-quote-left prl-message__quote"></i>
          <p class="prl-message__text" v-text="processingRequest.message"></p>
        </div>
      </div>

      <!-- Already responded -->
      <div v-if="alreadyResponded" class="prl-already-responded">
        <div class="prl-already-responded__icon">
          <i class="fas fa-check-double"></i>
        </div>
        <p class="prl-already-responded__title">Already Responded</p>
        <p class="prl-already-responded__sub">You have already responded to this request.</p>
        <router-link :to="'/chatbox/' + processingRequest.sender_id" class="prl-btn-gold mt-3">Open Messages</router-link>
      </div>

      <!-- Action buttons -->
      <template v-else>
        <p class="prl-actions-label">How would you like to respond?</p>
        <div class="prl-actions">

          <button class="prl-action-btn prl-action-btn--accept" :disabled="sending" @click="respond('available')">
            <span class="prl-action-btn__icon"><i class="fas fa-check-circle"></i></span>
            <span class="prl-action-btn__label">Available now</span>
            <span class="prl-action-btn__sub">Open chat immediately</span>
          </button>

          <button class="prl-action-btn prl-action-btn--later" :disabled="sending" @click="respond('later')">
            <span class="prl-action-btn__icon"><i class="fas fa-clock"></i></span>
            <span class="prl-action-btn__label">Later today</span>
            <span class="prl-action-btn__sub">Reply and open chat</span>
          </button>

          <button class="prl-action-btn prl-action-btn--schedule" :disabled="sending" @click="respond('schedule')">
            <span class="prl-action-btn__icon"><i class="fas fa-calendar-alt"></i></span>
            <span class="prl-action-btn__label">Schedule</span>
            <span class="prl-action-btn__sub">Coordinate a time in chat</span>
          </button>

          <button class="prl-action-btn prl-action-btn--unavailable" :disabled="sending" @click="respond('unavailable')">
            <span class="prl-action-btn__icon"><i class="fas fa-times-circle"></i></span>
            <span class="prl-action-btn__label">Unavailable</span>
            <span class="prl-action-btn__sub">Let them know kindly</span>
          </button>

        </div>

        <div v-if="sending" class="prl-sending">
          <i class="fas fa-spinner fa-spin mr-2"></i> Sending your response...
        </div>

        <div v-if="sent" style="padding: 0 24px 20px; gap: 6px">
          <span class="prl-action-btn__label" style="color: green">Response sent!</span>
          <router-link :to="'/chatbox/' + processingRequest.sender_id" class="prl-btn-gold ml-2">Open Messages</router-link>
        </div>
      </template>
    </div>

  </div>
</template>

<script>
import axios from '@/axios';

export default {
  name: 'ProcessingRequestLanding',
  data() {
    return {
      notification: null,
      processingRequest: null,
      loading: true,
      sending: false,
      sent: false,
    };
  },
  computed: {
    alreadyResponded() {
      return this.processingRequest && this.processingRequest.responded_at
    },
    senderLocation() {
      if (!this.processingRequest) {
        return null;
      }
      const parts = [this.processingRequest.sender?.city, this.processingRequest.sender?.country].filter(Boolean);
      return parts.join(', ');
    },
  },
  methods: {
    async respond(processingCode) {
      this.sending = true;
      await axios.post(`processingRequest/${this.processingRequest.id}/respond`, { type: processingCode });
      this.sending = false;
      this.sent = true;

      if (processingCode !== 'unavailable') {
        this.$router.push('/chatbox/' + this.processingRequest.sender_id);
      }
    },

    async fetchProcessingRequest(id) {
      this.loading = true
      this.processingRequest = await axios.get(`processingRequest/${id}`).then(x => x.data)
      this.loading = false
    },
  },
  mounted() {
    this.fetchProcessingRequest(this.$route.params.notificationId)
  },
};
</script>

<style scoped>
/* ── Page ── */
.prl-page {
  min-height: 100vh;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 40px 16px 60px;
}

.prl-loading,
.prl-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 40vh;
  gap: 10px;
}

.prl-empty__icon {
  font-size: 2.5rem;
  color: #ccc;
  margin-bottom: 8px;
}

.prl-empty__text {
  color: #aaa;
  margin: 0;
}

.pr-modal__header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 16px 20px 12px;
  border-bottom: 1px solid rgba(214, 182, 116, 0.2);
}
.pr-modal__header-icon { color: #d6b674; font-size: 1.1rem; }
.pr-modal__header-title { flex: 1; font-weight: 700; font-size: 1rem; color: #333; }

/* ── Card ── */
.prl-card {
  width: 100%;
  max-width: 520px;
  background: #fff;
  border-radius: 24px;
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.09);
  overflow: hidden;
}

/* ── Hero ── */
.prl-hero {
  display: flex;
  align-items: center;
  text-align: center;
  gap: 16px;
  padding: 18px 24px 12px;
  background: linear-gradient(160deg, #fffbf2 0%, #fff 100%);
  border-bottom: 1px solid #f0ece3;
}

.prl-hero__avatar-wrap {
  position: relative;
  margin-bottom: 14px;
}

.prl-hero__avatar {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid #fff;
  box-shadow: 0 4px 20px rgba(214, 182, 116, 0.35);
}

.prl-hero__badge {
  position: absolute;
  bottom: 0;
  right: -2px;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: linear-gradient(135deg, #d6b674 0%, #c4a55a 100%);
  border: 2px solid #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 0.65rem;
}

.prl-hero__name {
  font-size: 1.2rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
}

.prl-hero__location {
  font-size: 0.78rem;
  color: #b0a898;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 4px;
}

/* ── Message block ── */
.prl-message {
  margin: 24px 24px 0;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(214, 182, 116, 0.3);
  box-shadow: 0 4px 20px rgba(214, 182, 116, 0.12);
}

.prl-message__header {
  background: linear-gradient(135deg, #d6b674 0%, #c4a55a 100%);
  padding: 14px 18px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.prl-message__icon {
  color: rgba(255,255,255,0.85);
  font-size: 1rem;
  margin-top: 2px;
  flex-shrink: 0;
}

.prl-message__request {
  color: #fff;
  font-size: 0.88rem;
  line-height: 1.55;
  margin: 0;
}

.prl-message__request strong { font-weight: 700; }

.prl-message__body {
  background: #fffdf7;
  padding: 16px 18px;
  position: relative;
}

.prl-message__quote {
  position: absolute;
  top: 14px;
  left: 16px;
  color: rgba(214, 182, 116, 0.3);
  font-size: 1rem;
}

.prl-message__text {
  margin: 0;
  padding-left: 24px;
  color: #444;
  font-size: 0.93rem;
  line-height: 1.7;
  font-style: italic;
}

/* ── Actions ── */
.prl-actions-label {
  font-size: 0.7rem;
  font-weight: 700;
  color: #bbb;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  margin: 28px 24px 12px;
}

.prl-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  padding: 0 24px 28px;
}

.prl-action-btn {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  width: 100%;
  padding: 14px 16px;
  border-radius: 14px;
  border: 1px solid #eee;
  background: #fafafa;
  cursor: pointer;
  text-align: left;
  transition: transform 0.15s, box-shadow 0.15s, border-color 0.15s, background 0.15s;
}

.prl-action-btn__top {
  display: flex;
  align-items: center;
  gap: 8px;
}

.prl-action-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.07);
}

.prl-action-btn:disabled { opacity: 0.55; cursor: not-allowed; }

.prl-action-btn__icon { font-size: 1rem; flex-shrink: 0; }
.prl-action-btn__label { font-weight: 700; font-size: 0.88rem; color: #222; }
.prl-action-btn__sub   { font-size: 0.7rem; color: #aaa; }

.prl-action-btn--accept    { border-color: rgba(40,167,69,0.25);   background: rgba(40,167,69,0.03);   }
.prl-action-btn--accept:hover:not(:disabled)    { border-color: rgba(40,167,69,0.5);    background: rgba(40,167,69,0.06);   }
.prl-action-btn--accept .prl-action-btn__icon   { color: #28a745; }

.prl-action-btn--later     { border-color: rgba(214,182,116,0.35); background: rgba(214,182,116,0.04); }
.prl-action-btn--later:hover:not(:disabled)     { border-color: rgba(214,182,116,0.65); background: rgba(214,182,116,0.08); }
.prl-action-btn--later .prl-action-btn__icon    { color: #d6b674; }

.prl-action-btn--schedule  { border-color: rgba(0,123,255,0.2);    background: rgba(0,123,255,0.03);   }
.prl-action-btn--schedule:hover:not(:disabled)  { border-color: rgba(0,123,255,0.45);   background: rgba(0,123,255,0.06);   }
.prl-action-btn--schedule .prl-action-btn__icon { color: #007bff; }

.prl-action-btn--unavailable  { border-color: rgba(108,117,125,0.25); background: rgba(108,117,125,0.03); }
.prl-action-btn--unavailable:hover:not(:disabled) { border-color: rgba(108,117,125,0.45); background: rgba(108,117,125,0.06); }
.prl-action-btn--unavailable .prl-action-btn__icon { color: #6c757d; }

.prl-sending {
  text-align: center;
  padding: 0 24px 20px;
  font-size: 0.85rem;
  color: #aaa;
}

/* ── Already responded ── */
.prl-already-responded {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 36px 24px 32px;
  gap: 8px;
}

.prl-already-responded__icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: rgba(108, 117, 125, 0.07);
  border: 1px solid rgba(108, 117, 125, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  font-size: 1.5rem;
  margin-bottom: 8px;
}

.prl-already-responded__title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #333;
  margin: 0;
}

.prl-already-responded__sub {
  font-size: 0.86rem;
  color: #999;
  margin: 0;
  line-height: 1.5;
}

/* ── Shared button ── */
.prl-btn-gold {
  display: inline-block;
  background: linear-gradient(135deg, #d6b674 0%, #c4a55a 100%);
  color: #fff;
  border: none;
  border-radius: 24px;
  font-size: 0.85rem;
  font-weight: 600;
  padding: 9px 22px;
  text-decoration: none;
  transition: opacity 0.18s, transform 0.18s;
  cursor: pointer;
}

.prl-btn-gold:hover {
  opacity: 0.88;
  transform: translateY(-1px);
  color: #fff;
}

/* ── Responsive ── */
@media (max-width: 480px) {
  .prl-actions { grid-template-columns: 1fr; }
  .prl-card { border-radius: 18px; }
}
</style>
